import { adminApi } from "../api";

// Generic CRUD factory
export const makeCRUD = (base) => ({
  list: (params) => adminApi.get(base, { params }).then(r => r.data),
  get: (id) => adminApi.get(`${base}/${id}`).then(r => r.data),
  create: (p) => adminApi.post(base, p).then(r => r.data),
  update: (id, p) => adminApi.patch(`${base}/${id}`, p).then(r => r.data),
  delete: (id) => adminApi.delete(`${base}/${id}`).then(r => r.data),
});

export const articlesApi = makeCRUD("/admin/articles");
export const conditionsApi = makeCRUD("/admin/conditions");
export const faqsApi = makeCRUD("/admin/faqs");
export const guidelinesApi = makeCRUD("/admin/guidelines");
export const papersApi = makeCRUD("/admin/research-papers");
export const updatesApi = makeCRUD("/admin/research-updates");
export const summariesApi = makeCRUD("/admin/evidence-summaries");
export const referencesApi = makeCRUD("/admin/references");
export const treatmentsApi = makeCRUD("/admin/treatments");
export const symptomsApi = makeCRUD("/admin/symptoms");
export const investigationsApi = makeCRUD("/admin/investigations");
export const medicinesApi = makeCRUD("/admin/medicines");
export const trimestersApi = makeCRUD("/admin/trimesters");
export const videosApi = makeCRUD("/admin/videos");
export const patientGuidesApi = makeCRUD("/admin/patient-guides");
export const infographicsApi = makeCRUD("/admin/infographics");
export const booksApi = makeCRUD("/admin/books");
export const testimonialsApi = makeCRUD("/admin/testimonials");
export const mediaAppearancesApi = makeCRUD("/admin/media-appearances");
export const teachingSlidesApi = makeCRUD("/admin/teaching-slides");
export const clinicalToolsApi = makeCRUD("/admin/clinical-tools");
export const aiPromptsApi = makeCRUD("/admin/ai-prompts");
export const aiModelsApi = makeCRUD("/admin/ai-models");
export const derivativeRequestsApi = {
  list: (params = {}) => adminApi.get(`/admin/derivative-requests`, { params }).then(r => r.data),
  create: (payload) => adminApi.post(`/admin/derivative-requests`, payload).then(r => r.data),
  update: (id, payload) => adminApi.patch(`/admin/derivative-requests/${id}`, payload).then(r => r.data),
  remove: (id) => adminApi.delete(`/admin/derivative-requests/${id}`).then(r => r.data),
};
export const graphExplorerApi = {
  neighborhood: (entity_type, entity_id, hops = 1) =>
    adminApi.get(`/admin/graph-explorer/neighborhood`, { params: { entity_type, entity_id, hops } }).then(r => r.data),
};

// Group-B special actions
export const mediaExtraApi = {
  unlockMaster: (id, reason) => adminApi.post(`/admin/infographics/${id}/unlock-master`, { reason }).then(r => r.data),
  relockMaster: (id) => adminApi.post(`/admin/infographics/${id}/relock-master`).then(r => r.data),
  approveTestimonial: (id, approved, notes = "") => adminApi.post(`/admin/testimonials/${id}/approve`, { approved, review_notes: notes }).then(r => r.data),
};

// Entity links + graph taxonomy
export const linksApi = {
  forEntity: (etype, eid) => adminApi.get(`/admin/entity-links/for/${etype}/${eid}`).then(r => r.data),
  create: (p) => adminApi.post(`/admin/entity-links`, p).then(r => r.data),
  verify: (id, verified = true, notes = "") => adminApi.patch(`/admin/entity-links/${id}/verify`, { verified, notes }).then(r => r.data),
  remove: (id) => adminApi.delete(`/admin/entity-links/${id}`).then(r => r.data),
  updateNotes: (id, notes) => adminApi.patch(`/admin/entity-links/${id}`, { notes }).then(r => r.data),
};
export const graphApi = {
  taxonomy: () => adminApi.get(`/admin/graph/taxonomy`).then(r => r.data),
  searchEntities: (etype, q) => adminApi.get(`/admin/graph/entities/${etype}`, { params: q ? { q } : {} }).then(r => r.data),
};
export const archiveApi = {
  archive: (coll, id) => adminApi.post(`/admin/${coll}/${id}/archive`).then(r => r.data),
};

// Suggestions inbox + auto-suggest engine
export const suggestionsApi = {
  scan: (params = {}) => adminApi.post(`/admin/suggestions/scan`, null, { params }).then(r => r.data),
  list: (params = {}) => adminApi.get(`/admin/suggestions`, { params }).then(r => r.data),
  counts: () => adminApi.get(`/admin/suggestions/counts`).then(r => r.data),
  forEntity: (etype, eid) => adminApi.get(`/admin/suggestions/for/${etype}/${eid}`).then(r => r.data),
  accept: (id, notes = "") => adminApi.post(`/admin/suggestions/${id}/accept`, { notes }).then(r => r.data),
  reject: (id, notes = "") => adminApi.post(`/admin/suggestions/${id}/reject`, { notes }).then(r => r.data),
  merge: (id, notes = "") => adminApi.post(`/admin/suggestions/${id}/merge`, { notes }).then(r => r.data),
};

// Global admin search (used by command palette)
export const globalSearchApi = {
  query: (q, limit = 8) => adminApi.get(`/admin/search`, { params: { q, limit } }).then(r => r.data),
};

export const insightsApi = {
  listOn: (host, hostId) => adminApi.get(`/admin/clinical-insights/${host}/${hostId}`).then(r => r.data),
  create: (host, hostId, p) => adminApi.post(`/admin/clinical-insights/${host}/${hostId}`, p).then(r => r.data),
  update: (host, hostId, id, p) => adminApi.patch(`/admin/clinical-insights/${host}/${hostId}/${id}`, p).then(r => r.data),
  submitForApproval: (host, hostId, id) => adminApi.post(`/admin/clinical-insights/${host}/${hostId}/${id}/submit-for-approval`).then(r => r.data),
  approve: (host, hostId, id) => adminApi.post(`/admin/clinical-insights/${host}/${hostId}/${id}/approve`).then(r => r.data),
  archive: (host, hostId, id) => adminApi.post(`/admin/clinical-insights/${host}/${hostId}/${id}/archive`).then(r => r.data),
  remove: (host, hostId, id) => adminApi.delete(`/admin/clinical-insights/${host}/${hostId}/${id}`).then(r => r.data),
  pending: () => adminApi.get(`/admin/clinical-insights/pending-approval`).then(r => r.data),
};

export const versionsApi = {
  list: (coll, id) => adminApi.get(`/admin/versions/${coll}/${id}`).then(r => r.data),
  restore: (coll, id, vid) => adminApi.post(`/admin/versions/${coll}/${id}/${vid}/restore`).then(r => r.data),
};
export const previewApi = {
  issue: (coll, id) => adminApi.post(`/admin/preview-token/${coll}/${id}`).then(r => r.data),
};
export const refUsageApi = {
  get: (refId) => adminApi.get(`/admin/references/${refId}/usage`).then(r => r.data),
};

// Collection slug for backend paths (matches admin_content router prefixes)
export const TYPE_TO_COLLECTION = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  "research-papers": "research_papers", "research-updates": "research",
  "evidence-summaries": "evidence_summaries", references: "references",
  treatments: "treatments", symptoms: "symptoms", investigations: "investigations",
  medicines: "medicines", trimesters: "trimesters",
  videos: "videos", "patient-guides": "patient_guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", "media-appearances": "media_appearances",
  "teaching-slides": "teaching_slides", "clinical-tools": "clinical_tools",
  "ai-prompts": "ai_prompts", "ai-models": "ai_models",
};

// Public URL path for preview (matches public frontend routes)
export const TYPE_TO_PUBLIC_PATH = {
  articles: "articles", conditions: "conditions",
};

export const CONTENT_TYPES = {
  articles:            { label: "Articles",            api: articlesApi,   host: "articles",           display: "title",  hasInsights: true,  hasConnections: true },
  conditions:          { label: "Conditions",          api: conditionsApi, host: "conditions",         display: "name",   hasInsights: true,  hasConnections: true },
  faqs:                { label: "FAQs",                api: faqsApi,       host: null,                  display: "question", hasInsights: false, hasConnections: false },
  guidelines:          { label: "Guidelines",          api: guidelinesApi, host: "guidelines",         display: "title",  hasInsights: true,  hasConnections: true },
  "research-papers":   { label: "Research Papers",     api: papersApi,     host: null,                  display: "title",  hasInsights: false, hasConnections: true },
  "research-updates":  { label: "Research Updates",    api: updatesApi,    host: "research_updates",   display: "title",  hasInsights: true,  hasConnections: true },
  "evidence-summaries":{ label: "Evidence Summaries",  api: summariesApi,  host: "evidence_summaries", display: "title",  hasInsights: true,  hasConnections: true },
  references:          { label: "References",          api: referencesApi, host: null,                  display: "title",  hasInsights: false, hasConnections: false },
  treatments:          { label: "Treatments",          api: treatmentsApi, host: null,                  display: "name",   hasInsights: false, hasConnections: true },
  symptoms:            { label: "Symptoms",            api: symptomsApi,   host: null,                  display: "name",   hasInsights: false, hasConnections: true },
  investigations:      { label: "Investigations",      api: investigationsApi, host: null,              display: "name",   hasInsights: false, hasConnections: true },
  medicines:           { label: "Medicines",           api: medicinesApi,  host: null,                  display: "name",   hasInsights: false, hasConnections: true },
  trimesters:          { label: "Trimesters",          api: trimestersApi, host: null,                  display: "name",   hasInsights: false, hasConnections: true },
  videos:              { label: "Videos",              api: videosApi,     host: null,                  display: "title",  hasInsights: false, hasConnections: true },
  "patient-guides":    { label: "Patient Guides",      api: patientGuidesApi, host: null,               display: "title",  hasInsights: false, hasConnections: true },
  infographics:        { label: "Infographics",        api: infographicsApi, host: null,                display: "title",  hasInsights: false, hasConnections: true, hasMaster: true },
  books:               { label: "Books",               api: booksApi,      host: null,                  display: "title",  hasInsights: false, hasConnections: true },
  testimonials:        { label: "Testimonials",        api: testimonialsApi, host: null,                display: "patient_alias", hasInsights: false, hasConnections: true, requiresModeration: true },
  "media-appearances": { label: "Media Appearances",   api: mediaAppearancesApi, host: null,            display: "title",  hasInsights: false, hasConnections: true },
  "teaching-slides":   { label: "Teaching Slides",     api: teachingSlidesApi, host: null,              display: "title",  hasInsights: false, hasConnections: true },
  "clinical-tools":    { label: "Clinical Tools",      api: clinicalToolsApi, host: null,               display: "name",   hasInsights: false, hasConnections: true },
  "ai-prompts":        { label: "AI Prompts",          api: aiPromptsApi, host: null,                   display: "name",   hasInsights: false, hasConnections: true },
  "ai-models":         { label: "AI Models",           api: aiModelsApi, host: null,                    display: "name",   hasInsights: false, hasConnections: true },
};
